// The console's calls for plans, entitlements, tenant edits and usage (spec 15
// §5; M15-6, M15-7a), through the one generated client like the rest of
// platform-api.ts. Every mutation carries the caller's held Idempotency-Key.

import { client } from "../../../lib/api";
import type { components } from "@assessly/api-client";
import { idempotent, unwrap, type TenantDetail } from "./platform-api";

export type PlanView = components["schemas"]["PlatformPlanView"];
export type PlanCreateInput = components["schemas"]["PlatformPlanCreateRequest"];
export type PlanUpdateInput = components["schemas"]["PlatformPlanUpdateRequest"];
export type PlanLimitCode = components["schemas"]["PlanLimitCode"];
export type TenantEntitlements = components["schemas"]["PlatformTenantEntitlementsResponse"];
export type ModuleEntitlement = TenantEntitlements["modules"][number];
export type LimitEntitlement = TenantEntitlements["limits"][number];
export type TenantUpdateInput = components["schemas"]["PlatformTenantUpdateRequest"];
export type OverrideSetInput = components["schemas"]["PlatformOverrideSetRequest"];
export type TenantUsage = components["schemas"]["PlatformTenantUsageResponse"];
export type UsageMetric = components["schemas"]["UsageMetric"];

export async function listPlans(
  includeRetired: boolean,
  signal?: AbortSignal,
): Promise<readonly PlanView[]> {
  const result = await client.GET("/v1/platform/plans", {
    params: { query: includeRetired ? { include_retired: "true" } : {} },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load plans").items;
}

export async function createPlan(input: PlanCreateInput, key: string): Promise<PlanView> {
  const result = await client.POST("/v1/platform/plans", {
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not create the plan");
}

export async function updatePlan(
  id: string,
  input: PlanUpdateInput,
  key: string,
): Promise<PlanView> {
  const result = await client.PATCH("/v1/platform/plans/{id}", {
    params: { path: { id } },
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not save the plan");
}

export async function retirePlan(id: string, key: string): Promise<PlanView> {
  const result = await client.POST("/v1/platform/plans/{id}/retire", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not retire the plan");
}

export async function getEntitlements(
  id: string,
  signal?: AbortSignal,
): Promise<TenantEntitlements> {
  const result = await client.GET("/v1/platform/tenants/{id}/entitlements", {
    params: { path: { id } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the entitlements");
}

export async function setOverride(
  id: string,
  kind: "module" | "limit",
  code: string,
  input: OverrideSetInput,
  key: string,
): Promise<TenantEntitlements> {
  const result = await client.PUT("/v1/platform/tenants/{id}/overrides/{kind}/{code}", {
    params: { path: { id, kind, code } },
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not save the override");
}

export async function removeOverride(
  id: string,
  kind: "module" | "limit",
  code: string,
  reason: string,
  key: string,
): Promise<TenantEntitlements> {
  const result = await client.DELETE("/v1/platform/tenants/{id}/overrides/{kind}/{code}", {
    params: { path: { id, kind, code } },
    body: { reason },
    headers: idempotent(key),
  });
  return unwrap(result, "could not remove the override");
}

export async function updateTenant(
  id: string,
  input: TenantUpdateInput,
  key: string,
): Promise<TenantDetail> {
  const result = await client.PATCH("/v1/platform/tenants/{id}", {
    params: { path: { id } },
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not update the tenant");
}

export async function renameSubdomain(
  id: string,
  input: { new_subdomain: string; reason: string },
  key: string,
): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants/{id}/subdomain", {
    params: { path: { id } },
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not move the subdomain");
}

export async function getUsage(
  id: string,
  month: string | undefined,
  signal?: AbortSignal,
): Promise<TenantUsage> {
  const result = await client.GET("/v1/platform/tenants/{id}/usage", {
    params: { path: { id }, query: month === undefined ? {} : { month } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load usage");
}
